import {
  appendEquityExtraBenchmarkColumns,
  appendTxnBenchmarkColumns,
  EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
  TXN_BENCHMARK_COLUMN_ORDER,
  withTxnBenchmarkColumns,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  appendHoldingLevelCostColumns,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  FieldColumn,
  getColumnsForReitReportType,
  INVESTOR_COLUMN,
  REIT_ALL_COMMON_COLUMNS,
  REIT_MIS_COLUMNS,
  REIT_POINT_TO_POINT_REPORT,
  REIT_TABLE_SEGMENT,
  REIT_TAX_REPORT,
  REIT_TRANSACTION_COLUMNS,
  REIT_UNREALIZED_HOLDING_COLUMNS,
} from "./reitReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
]);

const UNREALIZED_HOLDING_TYPE_COLUMN: FieldColumn = {
  key: "type",
  value: REPORT_GAIN_TYPE_COLUMN_LABEL,
  locked: true,
  minWidth: 132,
};

const REPORT_DAYS_COLUMN: FieldColumn = { key: "days", value: "Avg. Days", minWidth: 88 };

const REPORT_COST_COLUMN_KEYS = ["stt", "stampDuty", "otherExpenses", "brokerage"] as const;

const REIT_ALL_REPORT_AFTER_ADJ_COLUMNS: FieldColumn[] = [
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [...without.slice(0, insertAt), UNREALIZED_CAGR_COLUMN, ...without.slice(insertAt)];
};

/** REIT hides folioNumber/folioNo/folio columns — matching aumsampaan REIT_HIDDEN_COLUMN_KEYS */
const REIT_HIDDEN_COLUMN_KEYS = new Set(["folioNumber", "folioNo", "folio"]);
const stripReitHiddenColumns = (cols: FieldColumn[]): FieldColumn[] =>
  cols.filter((c) => !REIT_HIDDEN_COLUMN_KEYS.has(c.key));

const withUnrealizedHoldingTypeColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (!cols.length) return cols;
  let result = stripReitHiddenColumns(
    cols.map((c) => (c.key === "type" ? { ...c, value: REPORT_GAIN_TYPE_COLUMN_LABEL } : c)),
  );
  if (result.some((c) => c.key === "type")) {
    return withUnrealizedCagrColumn(result);
  }
  const isinIdx = result.findIndex((c) => c.key === "isin");
  const nameIdx = result.findIndex((c) => c.key === "symbol" || c.key === "schemeName");
  const insertAt =
    isinIdx >= 0 ? isinIdx + 1 : nameIdx >= 0 ? nameIdx + 1 : 1;
  return withUnrealizedCagrColumn([
    ...result.slice(0, insertAt),
    UNREALIZED_HOLDING_TYPE_COLUMN,
    ...result.slice(insertAt),
  ]);
};

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName" || c.key === "symbol")) return cols;
  return [{ key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 }, ...cols];
};

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(c.key as (typeof REPORT_COST_COLUMN_KEYS)[number]),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [...withoutDays.slice(0, insertAt), daysCol, ...withoutDays.slice(insertAt)];
};

const REIT_P2P_REPORT_COLUMN_ORDER = [
  "symbol",
  "exchange",
  "transType",
  "units",
  "investedAmount",
  "currentAmount",
  "currentNAV",
  "avgPrice",
  "xirr",
  "cagr",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
  ...TXN_BENCHMARK_COLUMN_ORDER,
  ...EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
] as const;

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const orderColumnsByKeys = (
  columns: FieldColumn[],
  orderKeys: readonly string[],
): FieldColumn[] => {
  const order = new Map(orderKeys.map((key, idx) => [key, idx]));
  return [...columns].sort(
    (a, b) => (order.get(a.key) ?? 999) - (order.get(b.key) ?? 999),
  );
};

const orderTrailingColumnsAfterAdjCost = (
  columns: FieldColumn[],
  trailingColumns: FieldColumn[],
): FieldColumn[] => {
  const trailingKeySet = new Set(trailingColumns.map((c) => c.key));
  const without = columns.filter((c) => !trailingKeySet.has(c.key));
  const trailingCols = trailingColumns.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return existing ?? def;
  });
  const adjIdx = without.findIndex((c) => c.key === "adjustedCostPrice");
  const insertAt = adjIdx >= 0 ? adjIdx + 1 : without.length;
  return [...without.slice(0, insertAt), ...trailingCols, ...without.slice(insertAt)];
};

const appendReitBenchmarkAndCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildReitHoldingPreset = (): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(REIT_UNREALIZED_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumns(cols, REIT_TABLE_SEGMENT);
  cols = appendTxnBenchmarkColumns(cols);
  return appendEquityExtraBenchmarkColumns(cols);
};

const buildReitHoldingColumns = (): FieldColumn[] =>
  appendReitBenchmarkAndCostColumns(
    withUnrealizedHoldingTypeColumn(buildReitHoldingPreset()),
    { placeDaysBeforeStt: true },
  );

const buildReitRealizedColumns = (): FieldColumn[] => {
  let cols = withTxnMetadataAndBenchmarkColumns(
    getColumnsForReitReportType("Realized"),
    REIT_TABLE_SEGMENT,
    { includeBenchmarks: true },
  ) as FieldColumn[];
  cols = withUnrealizedCagrColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  return stripMemberNameColumn(stripReitHiddenColumns(cols));
};

const buildReitAllColumns = (): FieldColumn[] => {
  let cols = appendTxnBenchmarkColumns(
    appendHoldingLevelCostColumns(
      appendAdjustedCostPriceColumn(REIT_ALL_COMMON_COLUMNS),
      REIT_TABLE_SEGMENT,
    ),
  );
  cols = ensureReportDaysColumn(cols);
  const keys = new Set(cols.map((c) => c.key));
  REIT_ALL_REPORT_AFTER_ADJ_COLUMNS.forEach((col) => {
    if (!keys.has(col.key)) cols.push(col);
  });
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = orderTrailingColumnsAfterAdjCost(cols, REIT_ALL_REPORT_AFTER_ADJ_COLUMNS);
  return appendReitBenchmarkAndCostColumns(stripReitHiddenColumns(cols), {
    placeDaysBeforeStt: true,
  });
};

const buildReitTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    appendEquityExtraBenchmarkColumns(
      withTxnMetadataAndBenchmarkColumns(REIT_TRANSACTION_COLUMNS, REIT_TABLE_SEGMENT, {
        includeBenchmarks: true,
      }) as FieldColumn[],
    ),
  );
  cols = withUnrealizedCagrColumn(cols);
  return stripMemberNameColumn(cols);
};

const buildReitMisColumns = (): FieldColumn[] =>
  stripMemberNameColumn(appendAdjustedCostPriceColumn(REIT_MIS_COLUMNS));

const buildReitTaxColumns = (): FieldColumn[] =>
  stripMemberNameColumn(getColumnsForReitReportType(REIT_TAX_REPORT));

const buildReitP2pColumns = (): FieldColumn[] => {
  let cols = appendEquityExtraBenchmarkColumns(
    withTxnBenchmarkColumns(getColumnsForReitReportType(REIT_POINT_TO_POINT_REPORT)),
  );
  cols = stripMemberNameColumn(cols);
  return orderColumnsByKeys(cols, REIT_P2P_REPORT_COLUMN_ORDER);
};

export const getBaseColumnsForReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return buildReitHoldingColumns();
    case "Realized":
      return buildReitRealizedColumns();
    case "All":
      return buildReitAllColumns();
    case "Transaction":
      return buildReitTransactionColumns();
    case "MIS Report":
      return buildReitMisColumns();
    case REIT_TAX_REPORT:
      return buildReitTaxColumns();
    case REIT_POINT_TO_POINT_REPORT:
      return buildReitP2pColumns();
    default:
      return getColumnsForReitReportType(reportType);
  }
};

export const buildSelectableColumns = (reportType: string): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType),
];

export const buildEffectiveColumns = (
  reportType: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (reportType: string): string[] =>
  buildSelectableColumns(reportType).map((c) => c.key);
